import React, { useMemo } from 'react';
import { Alert, Card, Col, Empty, Row, Spin, Typography } from 'antd';

import { useMutation, useQuery } from '@tanstack/react-query';

import { useTradeBookStore } from '../../../../store';
import { TRADEBOOK } from '../../../../utilities/functions';

import RMultipleChart from './RMultipleChart';
import RMultipleTable from './RMultipleTable';
import SetSelector from './SetSelector';
import SimulationPanel from './SimulationPanel';
import SimulationResults from './SimulationResults';

import './styles.scss';

const { Text, Title } = Typography;

export default function ProfitabilityComparisonTab() {
    const {
        selectedPlan,
        selectedProfitabilitySet,
        setSelectedProfitabilitySet,
        simulationPlans,
        simulationTradeCount
    } = useTradeBookStore();

    // Fetch profitability summary for all sets
    const {
        isLoading: summaryLoading,
        data: summaryData,
        error: summaryError
    } = useQuery(
        ['profitability-summary', selectedPlan],
        () => TRADEBOOK.fetchProfitabilitySummary(selectedPlan),
        {
            enabled: !!selectedPlan,
            staleTime: 30000,
            refetchOnWindowFocus: true
        }
    );

    // Fetch detailed R-multiple analysis for selected set
    const { isLoading: analysisLoading, data: analysisData } = useQuery(
        [
            'r-multiple-analysis',
            selectedPlan,
            selectedProfitabilitySet?.setType,
            selectedProfitabilitySet?.setNumber
        ],
        () =>
            TRADEBOOK.fetchRMultipleAnalysis(
                selectedPlan,
                selectedProfitabilitySet.setType,
                selectedProfitabilitySet.setNumber
            ),
        {
            enabled: !!selectedPlan && !!selectedProfitabilitySet,
            staleTime: 30000
        }
    );

    // Simulation mutation
    const simulationMutation = useMutation(
        (data) => TRADEBOOK.simulatePartialStrategies(selectedPlan, data),
        {
            onError: (error) => {
                console.error('Simulation error:', error);
            }
        }
    );

    // Run simulation when parameters change
    const runSimulation = () => {
        if (!selectedProfitabilitySet) return;

        simulationMutation.mutate({
            setType: selectedProfitabilitySet.setType,
            setNumber: selectedProfitabilitySet.setNumber,
            tradeCount: simulationTradeCount,
            plans: simulationPlans.map((p) => ({
                planName: p.planName,
                partials: p.partials
            }))
        });
    };

    // Build available sets list from summary
    const availableSets = useMemo(() => {
        if (!summaryData) return [];

        const sets = [];

        (summaryData.strategySets || []).forEach((set) => {
            sets.push({
                label: `Strategy Set ${set.setNumber}`,
                value: { setType: 'strategy', setNumber: set.setNumber },
                trades: set.totalTrades
            });
        });

        (summaryData.noStrategySets || []).forEach((set) => {
            sets.push({
                label: `No Strategy Set ${set.setNumber}`,
                value: { setType: 'noStrategy', setNumber: set.setNumber },
                trades: set.totalTrades
            });
        });

        return sets;
    }, [summaryData]);

    // Show empty state when no trading plan is selected
    if (!selectedPlan) {
        return (
            <div className="profitability-empty">
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <div>
                            <Title level={5}>No Trading Plan Selected</Title>
                            <Text type="secondary">
                                Select a trading plan from the left sidebar to view Profitability
                                Comparison.
                            </Text>
                        </div>
                    }
                />
            </div>
        );
    }

    if (summaryLoading) {
        return (
            <div className="profitability-loading">
                <Spin size="large" />
                <Text>Loading Profitability Data...</Text>
            </div>
        );
    }

    if (summaryError) {
        return (
            <div className="profitability-error">
                <Alert
                    type="error"
                    message="Failed to load profitability data"
                    description="Please try again or check your connection."
                />
            </div>
        );
    }

    return (
        <div className="profitability-comparison-tab">
            <div className="profitability-header">
                <Title level={4}>{summaryData?.strategyName} - Profitability Comparison</Title>
            </div>

            <div className="profitability-content">
                {/* Set Selection Section */}
                <Card className="set-selection-card" size="small">
                    <SetSelector
                        availableSets={availableSets}
                        selectedSet={selectedProfitabilitySet}
                        onSelectSet={setSelectedProfitabilitySet}
                    />
                </Card>

                {selectedProfitabilitySet ? (
                    <>
                        {/* R-Multiple Analysis Section */}
                        <Row gutter={[16, 16]} className="analysis-section">
                            <Col xs={24} lg={12}>
                                <Card
                                    title="R-Multiple Distribution"
                                    className="chart-card"
                                    loading={analysisLoading}
                                >
                                    {analysisData && (
                                        <RMultipleChart data={analysisData.rMultipleAnalysis} />
                                    )}
                                </Card>
                            </Col>
                            <Col xs={24} lg={12}>
                                <Card
                                    title="R-Multiple Statistics"
                                    className="table-card"
                                    loading={analysisLoading}
                                >
                                    {analysisData && (
                                        <RMultipleTable
                                            data={analysisData.rMultipleAnalysis}
                                            totalTrades={analysisData.totalTrades}
                                        />
                                    )}
                                </Card>
                            </Col>
                        </Row>

                        {/* Simulation Section */}
                        <Card
                            title="Partial Exit Strategy Simulation"
                            className="simulation-card"
                            extra={
                                <Text type="secondary">
                                    Based on {analysisData?.totalTrades || 0} trades in this set
                                </Text>
                            }
                        >
                            <SimulationPanel
                                plans={simulationPlans}
                                tradeCount={simulationTradeCount}
                                onRunSimulation={runSimulation}
                                isLoading={simulationMutation.isLoading}
                            />

                            {simulationMutation.data && (
                                <SimulationResults results={simulationMutation.data} />
                            )}
                        </Card>
                    </>
                ) : (
                    <Card className="select-set-prompt">
                        <Empty description="Select a strategy set above to view R-multiple analysis and run simulations" />
                    </Card>
                )}
            </div>
        </div>
    );
}
